import { useEffect, useRef } from "react";

/**
 * Safe HTML5 <video> wrapper.
 *
 * Why this exists:
 *   Safari / WebKit raises `Can't find variable: EmptyRanges` when a
 *   <video> element is unmounted while it still has a source attached.
 *   The browser's internal `syncControl` -> `played` getter fires
 *   post-teardown and trips on its own missing `EmptyRanges` symbol.
 *
 *   The actual event that kills Safari is the media controller reading
 *   `played` on an element that still has an active playback timer.
 *   `pause()` clears that timer. That is enough — we do NOT try to
 *   clear `src` or call `load()` on unmount.
 *
 * Regression that forced this rewrite (Phase 6.6):
 *   React 18 StrictMode (dev mode in CRA) double-invokes effects.
 *   The cleanup runs between the two mounts. If the cleanup clears
 *   `src`, the next mount sees the same prop value and React's
 *   reconciler skips the DOM update — leaving `src=""` forever.
 *   Result: detail modal video opens BLACK with a MEDIA_ELEMENT_ERROR
 *   "Empty src attribute" and `videoWidth=0`.
 *
 *   `pause()` is idempotent and has no such reconciliation hazard.
 */
export function SafeVideo({ src, ...rest }) {
  const ref = useRef(null);

  useEffect(() => {
    const el = ref.current;
    return () => {
      if (!el) return;
      try {
        el.pause();
      } catch {
        /* element may already be detached — ignore */
      }
    };
  }, []);

  // Defensive: if the underlying element has no media yet, swallow
  // the `play` callback so Safari doesn't read `played` on a
  // half-initialised element.
  const onPlay = (e) => {
    if (!e.currentTarget || e.currentTarget.readyState === 0) return;
    rest.onPlay?.(e);
  };

  return <video ref={ref} src={src} {...rest} onPlay={onPlay} />;
}

export default SafeVideo;
